import React from "react";
import { Box, Image, Text, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import TicketButton from "./TicketButton";
import Ticket from "../images/Menu/Ticket.png";
// import SoldOut from "../images/Assets/sold_out.png";
import { TEXTS } from "../constants/texts";

interface CommonImageProps {
  onContextMenu: (e: { preventDefault: () => void }) => void;
  draggable: boolean;
}

const preventDefault = (e: { preventDefault: () => void }): void => {
  e.preventDefault();
};

const commonImageProps: CommonImageProps = {
  onContextMenu: preventDefault,
  draggable: false,
};

// InViewHookResponseの型定義
interface InViewHookResponse {
  ref: (node?: Element) => void;
  inView: boolean;
  entry?: IntersectionObserverEntry;
}

interface TicketSectionProps {
  refs: {
    ticketRef: InViewHookResponse;
    ticketContentRef: InViewHookResponse;
    generalSaleTitleRef: InViewHookResponse;
    generalSaleContentRef: InViewHookResponse;
    generalSaleButtonRef: InViewHookResponse;
    generalSale2TitleRef: InViewHookResponse;
    generalSale2ContentRef: InViewHookResponse;
    generalSale2ButtonRef: InViewHookResponse;
    piaContentRef: InViewHookResponse;
    piaButtonRef: InViewHookResponse;
    eplusContentRef: InViewHookResponse;
    eplusButtonRef: InViewHookResponse;
    cnContentRef: InViewHookResponse;
    cnButtonRef: InViewHookResponse;
    ticketContent8Ref: InViewHookResponse;
    ticketContent9Ref: InViewHookResponse;
    ticketContent10Ref: InViewHookResponse;
    ticketContent11Ref: InViewHookResponse;
    ticketContent16Ref: InViewHookResponse;
    [key: string]: InViewHookResponse;
  };
  animationStates: Record<string, "visible" | "hidden" | "instant">;
  slideInVariants: any;
  elementsAnimatedState: {
    ticketContent: boolean;
    ticketContent8: boolean;
    ticketContent9: boolean;
    ticketContent10: boolean;
    [key: string]: boolean;
  };
  scrollDirection: "up" | "down";
}

const TicketSection: React.FC<TicketSectionProps> = ({
  refs,
  animationStates,
  elementsAnimatedState,
  scrollDirection,
}) => {
  const hasSameDayRefs = Boolean(
    (refs as any)?.sameDayTitleRef && (refs as any)?.sameDayContentRef
  );
  // 統一されたアニメーションのバリアント定義
  const unifiedVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      transition: { duration: 0 },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: { duration: 0 },
    },
  };

  // 時間差アニメーション用のバリアント
  const staggerParentVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1,
      },
    },
    instant: {
      opacity: 1,
      transition: { duration: 0 },
    },
  };

  // 料金表示部分専用のvariants（透明度1.0）
  const priceVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      transition: { duration: 0 },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
    instant: {
      opacity: 1,
      y: 0,
      transition: { duration: 0 },
    },
  };

  // 共通のボタンスペーシング設定
  const buttonMotionDivStyle = { marginTop: "-5px" } as const;
  const ticketButtonBoxMt = {
    base: "-27px",
    sm: "-27px",
    md: "-39px",
    lg: "-42px",
    xl: "-42px",
    "2xl": "-42px",
  } as const;
  // 2次先行のボタン用に、スマホでわずかに大きい余白に調整
  const ticketButtonBoxMtSecond = {
    base: "-25px",
    sm: "-25px",
    md: "-39px",
    lg: "-42px",
    xl: "-42px",
    "2xl": "-42px",
  } as const;

  // オフィシャル先行が一度でも「表示完了」したかを記録
  const [officialAppeared, setOfficialAppeared] = React.useState(false);

  return (
    <Box
      id="ticket-section"
      pos="relative"
      w="100%"
      mb={{ base: "80px", sm: "90px", md: "100px", lg: "120px" }}
    >
      <Box
        id="ticket-image-anchor"
        h="0"
        scrollMarginTop={{
          base: "30px",
          sm: "0px",
          md: "60px",
          lg: "80px",
          xl: "80px",
          "2xl": "80px",
        }}
      />
      <Box
        ref={refs.ticketRef.ref}
        width="100%"
        textAlign="center"
        mb={{
          base: "40px",
          sm: "50px",
          md: "60px",
          lg: "80px",
        }}
      >
        <motion.div
          initial="hidden"
          animate={animationStates.ticket}
          variants={unifiedVariants}
        >
          <Image
            id="ticket-image"
            src={Ticket}
            alt="ticket"
            loading="eager"
            w={{
              base: "90%",
              sm: "80%",
              md: "80%",
              lg: "80%",
              xl: "70%",
              "2xl": "65%",
            }}
            h="auto"
            mx="auto"
            maxW={{
              base: "360px",
              sm: "450px",
              md: "700px",
              lg: "900px",
              xl: "1200px",
              "2xl": "1200px",
            }}
            display="block"
            {...commonImageProps}
          />
        </motion.div>
      </Box>

      {/* チケットのコンテンツ部分 */}
      <Box
        ref={refs.ticketContentRef.ref}
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
        }}
        mx="auto"
      >
        <motion.div
          initial="hidden"
          animate={
            scrollDirection === "up"
              ? animationStates.ticketContent === "hidden"
                ? "instant"
                : animationStates.ticketContent
              : animationStates.ticketContent
          }
          variants={staggerParentVariants}
        >
          <VStack spacing={2} align="center">
            {/* メインタイトル */}
            <motion.div variants={priceVariants}>
              <Text
                color="rgb(215, 200, 150)"
                fontSize={{
                  base: "14px",
                  sm: "20px",
                  md: "28px",
                  lg: "35px",
                  xl: "35px",
                }}
                fontWeight="bold"
                whiteSpace={{
                  base: "normal",
                  md: "nowrap",
                }}
                mt={{
                  base: "-30px",
                  sm: "-40px",
                  md: "-40px",
                  lg: "-60px",
                  xl: "-60px",
                }}
              >
                {TEXTS.Ticket.content[0]}
              </Text>
            </motion.div>

            {/* 価格情報 */}
            <motion.div variants={priceVariants}>
              <Flex
                gap={{
                  base: 2,
                  sm: 3,
                  md: 6,
                  lg: 10,
                  xl: 14,
                }}
                align="center"
                flexWrap="wrap"
                justifyContent="center"
                mt={{
                  base: "0",
                  sm: "0",
                  md: "2",
                  lg: "2",
                }}
              >
                {[1, 2].map((index) => (
                  <Text
                    key={`ticket-price-text-${index}`}
                    color="white"
                    fontSize={{
                      base: "14px",
                      sm: "20px",
                      md: "28px",
                      lg: "35px",
                      xl: "35px",
                    }}
                    fontWeight="bold"
                  >
                    {TEXTS.Ticket.content[index]}
                  </Text>
                ))}
              </Flex>
            </motion.div>

            {/* 下線付きテキスト */}
            <motion.div variants={priceVariants}>
              <Box
                color="white"
                fontSize={{
                  base: "10px",
                  sm: "10px",
                  md: "18px",
                  lg: "24px",
                  xl: "24px",
                }}
                borderBottom="1px solid white"
                pb={0}
                width="fit-content"
                mt={{
                  base: "0",
                  sm: "0",
                  md: "4",
                  lg: "4",
                }}
                textAlign="center"
                mx="auto"
              >
                {TEXTS.Ticket.content[4]}
              </Box>
            </motion.div>

            {/* 先行販売 */}
            <Box
              bg="rgb(91, 63, 48)"
              px={{
                base: 4,
                sm: 6,
                md: 8,
                lg: 10,
              }}
              py={{
                base: 1,
                sm: 1,
                md: 1,
                lg: 1,
              }}
              mt={{
                base: "2",
                sm: "4",
                md: "6",
                lg: "10",
              }}
              width="90%"
              mx="auto"
            >
              <motion.div
                ref={refs.generalSaleTitleRef.ref}
                initial="hidden"
                animate={
                  refs.generalSaleTitleRef.inView
                    ? scrollDirection === "up"
                      ? "instant"
                      : "visible"
                    : scrollDirection === "up"
                    ? "instant"
                    : "hidden"
                }
                variants={{
                  hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: 0.5,
                      ease: "easeOut",
                      delay: 0.1,
                    },
                  },
                  instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                }}
                onAnimationComplete={() => setOfficialAppeared(true)}
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "14px",
                    sm: "20px",
                    md: "28px",
                    lg: "35px",
                    xl: "35px",
                  }}
                  fontWeight="bold"
                  textAlign="center"
                >
                  {TEXTS.TicketSales.content[0]}
                </Text>
              </motion.div>
            </Box>

            <Box
              bg="rgb(105, 82, 63)"
              width="90%"
              mx="auto"
              mt={-2}
              minHeight={{
                base: "80px",
                sm: "80px",
                md: "140px",
                lg: "160px",
              }}
              py={{ base: 0, sm: 0, md: 1, lg: 2, xl: 2 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <VStack
                spacing={3}
                align="center"
                width="100%"
                position="relative"
              >
                {/* 販売時期 */}
                <motion.div
                  ref={refs.generalSaleContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSaleContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.16,
                        staggerChildren: 0.12,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.generalSaleContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.12,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "12px",
                          sm: "18px",
                          md: "24px",
                          lg: "35px",
                          xl: "35px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        mt={{ base: 3, sm: 3, md: 0, lg: 0, xl: 0 }}
                      >
                        <Flex align="center" justify="center" gap={2}>
                          {TEXTS.TicketSales.content[1]}
                          <Text
                            fontSize={{
                              base: "12px",
                              sm: "13px",
                              md: "20px",
                              lg: "24px",
                              xl: "24px",
                            }}
                            color="white"
                            ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                          >
                            {TEXTS.TicketSales.content[8]}
                          </Text>
                        </Flex>
                      </Text>
                    </motion.div>

                    {/* 日時情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.generalSaleContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                      onAnimationComplete={() => setOfficialAppeared(true)}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.content[2]}
                      </Text>
                    </motion.div>
                  </VStack>
                </motion.div>

                {/* クローズボタン（別トリガー） */}
                <motion.div
                  ref={refs.generalSaleButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSaleButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={buttonMotionDivStyle}
                >
                  <Box mt={ticketButtonBoxMt}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>

                {/* 2次先行セクション */}
                <motion.div
                  ref={refs.ticketContent16Ref.ref}
                  initial="hidden"
                  animate={
                    officialAppeared
                      ? refs.ticketContent16Ref.inView
                        ? scrollDirection === "up"
                          ? "instant"
                          : "visible"
                        : scrollDirection === "up"
                        ? "instant"
                        : "hidden"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 20, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "12px",
                      sm: "18px",
                      md: "24px",
                      lg: "35px",
                      xl: "35px",
                    }}
                    fontWeight="bold"
                    textAlign="center"
                    mt={{ base: -3, sm: -3, md: 0, lg: 0, xl: 0 }}
                  >
                    <Flex align="center" justify="center" gap={2}>
                      {TEXTS.TicketSales.SecondRound[0]}
                      <Text
                        fontSize={{
                          base: "12px",
                          sm: "13px",
                          md: "20px",
                          lg: "24px",
                          xl: "24px",
                        }}
                        color="white"
                        ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                      >
                        {TEXTS.TicketSales.content[8]}
                      </Text>
                    </Flex>
                  </Text>

                  <Text
                    color="white"
                    fontSize={{
                      base: "10px",
                      sm: "12px",
                      md: "18px",
                      lg: "20px",
                      xl: "24px",
                      "2xl": "24px",
                    }}
                    textAlign="center"
                    mb={{ base: 0, sm: 0, md: 0 }}
                  >
                    {TEXTS.TicketSales.SecondRound[1]}
                  </Text>
                </motion.div>
                <motion.div style={buttonMotionDivStyle}>
                  <Box mt={ticketButtonBoxMtSecond}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>
              </VStack>
            </Box>

            {/* 一般販売 */}
            <Box
              bg="rgb(91, 63, 48)"
              px={{
                base: 4,
                sm: 6,
                md: 8,
                lg: 10,
              }}
              py={{
                base: 1,
                sm: 1,
                md: 1,
                lg: 1,
              }}
              mt={{
                base: "2",
                sm: "4",
                md: "6",
                lg: "10",
              }}
              width="90%"
              mx="auto"
            >
              <motion.div
                ref={refs.generalSale2TitleRef.ref}
                initial="hidden"
                animate={
                  refs.generalSale2TitleRef.inView
                    ? scrollDirection === "up"
                      ? "instant"
                      : "visible"
                    : scrollDirection === "up"
                    ? "instant"
                    : "hidden"
                }
                variants={{
                  hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: "easeOut", delay: 0.1 },
                  },
                  instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                }}
              >
                <Text
                  color="white"
                  fontSize={{
                    base: "12px",
                    sm: "18px",
                    md: "24px",
                    lg: "35px",
                    xl: "35px",
                  }}
                  fontWeight="bold"
                  textAlign="center"
                >
                  {TEXTS.TicketSales.content[4]}
                </Text>
              </motion.div>
            </Box>

            <Box
              bg="rgb(105, 82, 63)"
              width="90%"
              mx="auto"
              mt={-2}
              minHeight={{
                base: "80px",
                sm: "80px",
                md: "140px",
                lg: "160px",
              }}
              py={{ base: 0, sm: 0, md: 1, lg: 2, xl: 2 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <VStack
                spacing={3}
                align="center"
                width="100%"
                position="relative"
              >
                {/* ローチケ */}
                <motion.div
                  ref={refs.generalSale2ContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSale2ContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.16,
                        staggerChildren: 0.12,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.generalSale2ContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.12,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "14px",
                          sm: "20px",
                          md: "28px",
                          lg: "35px",
                          xl: "35px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        mt={{ base: 3, sm: 3, md: 0, lg: 0, xl: 0 }}
                      >
                        <Flex align="center" justify="center" gap={2}>
                          {TEXTS.TicketSales.Lowticket[0]}
                          <Text
                            fontSize={{
                              base: "12px",
                              sm: "13px",
                              md: "20px",
                              lg: "24px",
                              xl: "24px",
                            }}
                            color="white"
                            ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                          >
                            {TEXTS.TicketSales.content[9]}
                          </Text>
                        </Flex>
                      </Text>
                    </motion.div>

                    {/* Lコード情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.generalSale2ContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.Lowticket[1]}
                      </Text>
                    </motion.div>

                    {/* 日時情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.generalSale2ContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.content[6]}
                      </Text>
                    </motion.div>
                  </VStack>
                </motion.div>

                {/* ボタン */}
                <motion.div
                  ref={refs.generalSale2ButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.generalSale2ButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={buttonMotionDivStyle}
                >
                  <Box mt={ticketButtonBoxMt}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>

                {/* ぴあ */}
                <motion.div
                  ref={refs.piaContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.piaContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.16,
                        staggerChildren: 0.12,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.piaContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.12,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "14px",
                          sm: "20px",
                          md: "28px",
                          lg: "35px",
                          xl: "35px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        mt={{ base: -3, sm: -3, md: 0, lg: 0, xl: 0 }}
                      >
                        <Flex align="center" justify="center" gap={2}>
                          {TEXTS.TicketSales.pia[0]}
                          <Text
                            fontSize={{
                              base: "12px",
                              sm: "13px",
                              md: "20px",
                              lg: "24px",
                              xl: "24px",
                            }}
                            color="white"
                            ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                          >
                            {TEXTS.TicketSales.content[9]}
                          </Text>
                        </Flex>
                      </Text>
                    </motion.div>

                    {/* Pコード情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.piaContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.pia[1]}
                      </Text>
                    </motion.div>

                    {/* 日時情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.piaContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.content[6]}
                      </Text>
                    </motion.div>
                  </VStack>
                </motion.div>

                {/* ボタン */}
                <motion.div
                  ref={refs.piaButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.piaButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={buttonMotionDivStyle}
                >
                  <Box mt={ticketButtonBoxMt}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>

                {/* イープラス */}
                <motion.div
                  ref={refs.eplusContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.eplusContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.16,
                        staggerChildren: 0.12,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.eplusContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.12,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "14px",
                          sm: "20px",
                          md: "28px",
                          lg: "35px",
                          xl: "35px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        mt={{ base: -3, sm: -3, md: 0, lg: 0, xl: 0 }}
                      >
                        <Flex align="center" justify="center" gap={2}>
                          {TEXTS.TicketSales.ePlus[0]}
                          <Text
                            fontSize={{
                              base: "12px",
                              sm: "13px",
                              md: "20px",
                              lg: "24px",
                              xl: "24px",
                            }}
                            color="white"
                            ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                          >
                            {TEXTS.TicketSales.content[9]}
                          </Text>
                        </Flex>
                      </Text>
                    </motion.div>

                    {/* 日時情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.eplusContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.content[6]}
                      </Text>
                    </motion.div>
                  </VStack>
                </motion.div>

                {/* ボタン */}
                <motion.div
                  ref={refs.eplusButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.eplusButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={buttonMotionDivStyle}
                >
                  <Box mt={ticketButtonBoxMt}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>

                {/* CNプレイガイド */}
                <motion.div
                  ref={refs.cnContentRef.ref}
                  initial="hidden"
                  animate={
                    refs.cnContentRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        delayChildren: 0.16,
                        staggerChildren: 0.12,
                      },
                    },
                    instant: { opacity: 1, transition: { duration: 0 } },
                  }}
                >
                  <VStack spacing={1} width="100%">
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.cnContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.12,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "14px",
                          sm: "20px",
                          md: "28px",
                          lg: "35px",
                          xl: "35px",
                        }}
                        fontWeight="bold"
                        textAlign="center"
                        mt={{ base: -3, sm: -3, md: 0, lg: 0, xl: 0 }}
                      >
                        <Flex align="center" justify="center" gap={2}>
                          {TEXTS.TicketSales.CnPlayGuide[0]}
                          <Text
                            fontSize={{
                              base: "12px",
                              sm: "13px",
                              md: "20px",
                              lg: "24px",
                              xl: "24px",
                            }}
                            color="white"
                            ml={{ base: -1, sm: -2, md: 0, lg: 0, xl: 0 }}
                          >
                            {TEXTS.TicketSales.content[9]}
                          </Text>
                        </Flex>
                      </Text>
                    </motion.div>

                    {/* 問い合わせ問い合わせ情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.cnContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.CnPlayGuide[1]}
                        <br />
                        {TEXTS.TicketSales.CnPlayGuide[2]}
                      </Text>
                    </motion.div>

                    {/* 日時情報 */}
                    <motion.div
                      initial="hidden"
                      animate={
                        refs.cnContentRef.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden"
                      }
                      variants={{
                        hidden: {
                          opacity: 0,
                          y: 8,
                          transition: { duration: 0 },
                        },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.4,
                            ease: "easeOut",
                            delay: 0.22,
                          },
                        },
                        instant: {
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0 },
                        },
                      }}
                    >
                      <Text
                        color="white"
                        fontSize={{
                          base: "10px",
                          sm: "12px",
                          md: "18px",
                          lg: "20px",
                          xl: "24px",
                          "2xl": "24px",
                        }}
                        textAlign="center"
                      >
                        {TEXTS.TicketSales.content[6]}
                      </Text>
                    </motion.div>
                  </VStack>
                </motion.div>

                {/* ボタン */}
                <motion.div
                  ref={refs.cnButtonRef.ref}
                  initial="hidden"
                  animate={
                    refs.cnButtonRef.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden"
                  }
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.45,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                  style={buttonMotionDivStyle}
                >
                  <Box mt={ticketButtonBoxMt}>
                    <TicketButton
                      text={TEXTS.TicketSales.content[10]}
                      color="#3C3C3C"
                      isActive={false}
                    />
                  </Box>
                </motion.div>
              </VStack>
            </Box>
          </VStack>
        </motion.div>
      </Box>

      {/* 当日券 */}
      <Box
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
        }}
        mx="auto"
      >
        <Box
          bg="rgb(91, 63, 48)"
          px={{
            base: 4,
            sm: 6,
            md: 8,
            lg: 10,
          }}
          py={{
            base: 1,
            sm: 1,
            md: 1,
            lg: 1,
          }}
          mt={{
            base: "2",
            sm: "4",
            md: "6",
            lg: "10",
          }}
          width="90%"
          mx="auto"
        >
          <motion.div
            ref={refs.sameDayTitleRef?.ref}
            initial="hidden"
            {...(hasSameDayRefs
              ? {
                  animate: refs.sameDayTitleRef?.inView
                    ? scrollDirection === "up"
                      ? "instant"
                      : "visible"
                    : scrollDirection === "up"
                    ? "instant"
                    : "hidden",
                }
              : {
                  whileInView: "visible" as const,
                  viewport: { once: true, amount: 0.4 } as const,
                })}
            variants={{
              hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.5, ease: "easeOut", delay: 0.1 },
              },
              instant: { opacity: 1, y: 0, transition: { duration: 0 } },
            }}
          >
            <Text
              color="white"
              fontSize={{
                base: "12px",
                sm: "18px",
                md: "24px",
                lg: "35px",
                xl: "35px",
              }}
              fontWeight="bold"
              textAlign="center"
              position="relative"
              top="-5px"
            >
              {TEXTS.Ticket.SameDayTicket[0]}
            </Text>
          </motion.div>
        </Box>

        <Box
          bg="rgb(105, 82, 63)"
          width="90%"
          mx="auto"
          mt={-2}
          minHeight={{
            base: "80px",
            sm: "80px",
            md: "140px",
            lg: "160px",
          }}
          py={{ base: 0, sm: 0, md: 1, lg: 2, xl: 2 }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <VStack spacing={3} align="center" width="100%" position="relative">
            <motion.div
              ref={refs.sameDayContentRef?.ref}
              initial="hidden"
              {...(hasSameDayRefs
                ? {
                    animate: refs.sameDayContentRef?.inView
                      ? scrollDirection === "up"
                        ? "instant"
                        : "visible"
                      : scrollDirection === "up"
                      ? "instant"
                      : "hidden",
                  }
                : {
                    whileInView: "visible" as const,
                    viewport: { once: true, amount: 0.25 } as const,
                  })}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { delayChildren: 0.16, staggerChildren: 0.12 },
                },
                instant: { opacity: 1, transition: { duration: 0 } },
              }}
            >
              <VStack spacing={1} width="100%">
                {/* 注意事項 [3..6] */}
                <motion.div
                  initial="hidden"
                  {...(hasSameDayRefs
                    ? {
                        animate: refs.sameDayContentRef?.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden",
                      }
                    : {})}
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut",
                        delay: 0.12,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "10px",
                      sm: "12px",
                      md: "16px",
                      lg: "18px",
                      xl: "22px",
                      "2xl": "24px",
                    }}
                    textAlign="center"
                    mt={{ base: 2, sm: 2, md: 3, lg: 3 }}
                    whiteSpace={{ base: "pre-line", xl: "normal" }}
                  >
                    {TEXTS.Ticket.SameDayTicket[3]}
                    <br />
                    {TEXTS.Ticket.SameDayTicket[4]}
                    <br />
                    {TEXTS.Ticket.SameDayTicket[5]}
                    <br />
                    {TEXTS.Ticket.SameDayTicket[6]}
                  </Text>
                </motion.div>

                {/* 価格 [1] */}
                <motion.div
                  initial="hidden"
                  {...(hasSameDayRefs
                    ? {
                        animate: refs.sameDayContentRef?.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden",
                      }
                    : {})}
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut",
                        delay: 0.18,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "14px",
                      sm: "20px",
                      md: "28px",
                      lg: "35px",
                      xl: "35px",
                    }}
                    fontWeight="bold"
                    textAlign="center"
                    mt={{ base: 2, sm: 2, md: 2 }}
                  >
                    {TEXTS.Ticket.SameDayTicket[1]}
                  </Text>
                </motion.div>

                {/* 価格 [2] */}
                <motion.div
                  initial="hidden"
                  {...(hasSameDayRefs
                    ? {
                        animate: refs.sameDayContentRef?.inView
                          ? scrollDirection === "up"
                            ? "instant"
                            : "visible"
                          : scrollDirection === "up"
                          ? "instant"
                          : "hidden",
                      }
                    : {})}
                  variants={{
                    hidden: { opacity: 0, y: 8, transition: { duration: 0 } },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.4,
                        ease: "easeOut",
                        delay: 0.24,
                      },
                    },
                    instant: { opacity: 1, y: 0, transition: { duration: 0 } },
                  }}
                >
                  <Text
                    color="white"
                    fontSize={{
                      base: "14px",
                      sm: "20px",
                      md: "28px",
                      lg: "35px",
                      xl: "35px",
                    }}
                    fontWeight="bold"
                    textAlign="center"
                    mb={{ base: 3, sm: 4, md: 4, lg: 4 }}
                  >
                    {TEXTS.Ticket.SameDayTicket[2]}
                  </Text>
                </motion.div>
              </VStack>
            </motion.div>
          </VStack>
        </Box>
      </Box>

      {/* ここから独立したアニメーションを持つテキストセクション */}
      <Box
        width="100%"
        maxW={{
          base: "95%",
          sm: "90%",
          md: "80%",
          lg: "70%",
        }}
        mx="auto"
        mt={{
          base: 4,
          sm: 4,
          md: 8,
          lg: 10,
        }}
      >
        <Flex
          gap={{
            base: 2,
            sm: 2,
            md: 8,
            lg: 10,
          }}
          flexDirection={{
            base: "column",
            md: "row",
          }}
          align={{
            base: "center",
            md: "flex-start",
          }}
          justify="center"
          width="100%"
        >
          <motion.div
            ref={refs.ticketContent8Ref.ref}
            initial="hidden"
            animate={
              refs.ticketContent8Ref.inView ||
              elementsAnimatedState.ticketContent8
                ? "visible"
                : "hidden"
            }
            variants={unifiedVariants}
            onAnimationComplete={() => {
              if (refs.ticketContent8Ref.inView) {
                elementsAnimatedState.ticketContent8 = true;
              }
            }}
          >
            <Text
              color="white"
              fontSize={{
                base: "10px",
                sm: "10px",
                md: "16px",
                lg: "20px",
                xl: "22px",
                "2xl": "22px",
              }}
              border="1px solid white"
              px={2}
              py={1}
              height="fit-content"
            >
              {TEXTS.Ticket.content[8]}
            </Text>
          </motion.div>
          <Box mt={{ base: 0, md: "-2" }}>
            <Text
              color="white"
              fontSize={{
                base: "14px",
                sm: "20px",
                md: "20px",
                lg: "24px",
                xl: "35px",
                "2xl": "35px",
              }}
              fontWeight="bold"
            >
              <motion.div
                ref={refs.ticketContent9Ref.ref}
                initial="hidden"
                animate={
                  refs.ticketContent9Ref.inView ||
                  elementsAnimatedState.ticketContent9
                    ? "visible"
                    : "hidden"
                }
                variants={unifiedVariants}
                onAnimationComplete={() => {
                  if (refs.ticketContent9Ref.inView) {
                    elementsAnimatedState.ticketContent9 = true;
                  }
                }}
              >
                {TEXTS.Ticket.content[9]}
              </motion.div>
            </Text>
            <Text
              color="white"
              fontSize={{
                base: "10px",
                sm: "10px",
                md: "10px",
                lg: "16px",
                xl: "16px",
                "2xl": "16px",
              }}
            >
              <motion.div
                ref={refs.ticketContent10Ref.ref}
                initial="hidden"
                animate={
                  refs.ticketContent10Ref.inView ||
                  elementsAnimatedState.ticketContent10
                    ? "visible"
                    : "hidden"
                }
                variants={unifiedVariants}
                onAnimationComplete={() => {
                  if (refs.ticketContent10Ref.inView) {
                    elementsAnimatedState.ticketContent10 = true;
                  }
                }}
              >
                {TEXTS.Ticket.content[10]}
              </motion.div>
            </Text>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default TicketSection;
